import { BackendURLContantas } from "@/constants/URLConstants";
import { axios } from "@/lib/axios";
import { AxiosResponse } from "axios";
import { UserIconFile } from "../user";
import { FollowingImagesRequest } from "../follower";
import { QueryOperator } from "@/constants/AppConstants";
import {
  FILTER_IMAGE_CATEGORY_TYPE,
  FilterImageCategory,
  PageCategory,
} from "@/components/features/gallery/hooks/useGalleryCategory";
import { GroupDataResponse } from "../group";
import { ContestResponseData } from "../contest";

export type ImageOrderType = "average_score" | "created_at" | "total_score";

export type ImageSearchScreen = "public" | "settings";
export type OrderBy = "desc" | "asc";

export type ImageSearchRequest = {
  pageCategory: PageCategory;
  filterImageCategory: FilterImageCategory;
  requestUserId?: string | null;

  /**
   * 既存用途: 単一投稿者UUIDで絞り込む
   */
  filterByUserId?: string | null;

  /**
   * 新規用途: 投稿画像に紐づく users.user_id で絞り込む
   */
  filterByAuthorUserId?: string | null;

  targetUserIds?: string[] | null;
  filterByGroupId?: number | null;
  isLocked?: boolean | null;
  lockedByUserId?: string | null;
  order?: ImageOrderType;
  orderBy?: OrderBy;
  perPage?: number;
  page?: number;
  refreshInterval?: number;
  screen?: ImageSearchScreen;
};

export type ImageOrderCol =
  | "average_score"
  | "total_score"
  | "posts"
  | "created_at";

export type ImageRankingRequest = {
  myUserId?: string | null;
  userId?: string | null;
  groupId?: number | null;
};

export type ImageDeleteEventResponse = {
  imageId: string;
  posts: number;
  averageScore: number | null;
  totalScore: number;
};

export type ImagePostEventResponse = {
  image: ImageResponseData;
  posts: number;
  averageScore: number | null;
  totalScore: number;
};

export type ImageDeleteRequest = {
  uuid: string;
};

export type ImagePostRequest = {
  file: string | null;
  filename: string | null;
  mimeType: string | null;
  city: string;
  country: string;
  latitude: number;
  longitude: number;
  province: string;
  suburb: string;
  userId: string;
  score: number;
  groupId?: number | null;
};

export type ImageFile = {
  id: number;
  fileUrl: string;
  fileName: string;
  createdAt: string;
  updatedAt: string;
  summaryFileUrl: string | null;
};

type UserProfile = {
  id: number;
  userIconFileId: number | null;
  email: string;
  uuid: string;
  testimonials: string | null;
  hideName: boolean | null;
  name?: string;
  userId: string;
  userIconFile: UserIconFile;
  createdAt: string;
  updatedAt: string;
};

export type ImageResponseData = {
  id: number;
  userUuid: string;
  imageFileId: number;
  scoreExists: boolean;
  myScore: number;
  uuid: string;
  averageScore: number | null;
  totalScore: number | null;
  posts: number | null;
  city: string;
  country: string;
  province: string;
  suburb: string;
  latitude: number;
  longitude: number;
  timestamp: number;
  createdAt: string;
  updatedAt: string;
  imageFile: ImageFile;
  userProfile: UserProfile;
  following: boolean;
  userId: string;
  joinContest: boolean;
  group: GroupDataResponse | null;
  isInEntryPeriod?: boolean;
  isContestOpen?: boolean;
  isContestClosed?: boolean;
  contest: ContestResponseData | null;
  isLocked: boolean;
  lockedBy?: string | null;
  lockedAt?: string | null;
  lockReason?: string | null;
};

export type ImageResponse = {
  data: ImageResponseData[];
  currentPage: number;
  lastPage: number;
  total: number;
  lockedCount: number;
};

type ImagePostResponse = {
  message: string;
  ok: boolean;
};

type ImageTotalResponse = {
  total: number | null;
};

type ImageAvgResponse = {
  avg: number | null;
};

type ImageDeleteResponse = {
  message: string;
  ok: boolean;
  data: {
    fileName: string;
    userUuid: string;
    imageUuid: string;
  };
};

export type ImageShowRequest = {
  imageUuid: string;
  authUuid: string;
};

export type ImageCanShowScoreBarOnPostRequest = {
  groupId: number | null;
};

export type ImageCanShowScoreBarOnPostResponse = {
  canShowScoreBarOnPost: boolean;
};

type ImageWhere = "created_at";

export type ImageFindRequest = {
  uuid: string;
};

export type ImageExcludeFromGroupRequest = {
  imageId: number;
  groupId: number;
};

export type ImageExcludeFromGroupResponse = {
  message: string;
};

export type ImageScoreExistsRequest = {
  userUuid: string;
  imageUuid: string;
};

export type ImageScoreExistsReponse = {
  scoreExists: boolean;
};

export type ImageBulkActionType = "lock" | "unlock" | "delete";

export type ImageBulkActionRequest = {
  imageIds: number[];
  action: ImageBulkActionType;
  actor_uuid: string;
  lockReason?: string | null;
};

export type ImageBulkActionResponse = {
  message: string;
  affected: number;
  action: ImageBulkActionType;
  ids: number[];
};

/**
 * 追加: 指定ユーザーのロック画像件数取得
 */
export type ImageLockedCountByUserRequest = {
  userUuid: string;
};

export type ImageLockedCountByUserResponse = {
  lockedCount: number;
  hasLockedImages: boolean;
};

const validImageTotalResponse = (arg: unknown): boolean => {
  const res = arg as ImageTotalResponse;
  return typeof res.total === "number" || res.total === null;
};

const validImageAvgResponse = (arg: unknown): boolean => {
  const res = arg as ImageAvgResponse;
  return typeof res.avg === "number" || res.avg === null;
};

export type ImagesResponseData = {
  items?: ImageResponseData[];
  isLoading: boolean;
  isError: boolean;
  loadMore: () => void;
  hasMore: boolean;
  isValidating: boolean;
  lockedCound?: number;
};

export type Chat = {
  id: number;
  message: string;
  createdAt: string;
  updatedAt: string;
};

export type Chats = Chat[];

const normalizeImageSearchRequest = (
  filters: Partial<ImageSearchRequest>
): ImageSearchRequest => {
  return {
    pageCategory: "home",
    filterImageCategory: FILTER_IMAGE_CATEGORY_TYPE.LATEST,
    order: "created_at",
    orderBy: "desc",
    screen: "public",
    perPage: 20,
    page: 1,
    requestUserId: null,
    filterByUserId: null,
    filterByAuthorUserId: null,
    targetUserIds: null,
    filterByGroupId: null,
    isLocked: null,
    lockedByUserId: null,
    refreshInterval: 6000,
    ...filters,
  };
};

export type PaginatedImagesResponseData = {
  items?: ImageResponseData[];
  isLoading: boolean;
  isError: boolean;
  isValidating: boolean;
  total: number;
  currentPage: number;
  lastPage: number;
  refetch: () => Promise<ImageResponse | undefined>;
};

export const ImageAPI = {
  post: async (imagePostRequest: ImagePostRequest): Promise<ImagePostResponse> => {
    const { data } = await axios.post<
      ImagePostRequest,
      AxiosResponse<ImagePostResponse>
    >(BackendURLContantas.IMAGE.POST.PATH_NAME, imagePostRequest);

    return data;
  },

  delete: async (
    imageDeleteRequest: ImageDeleteRequest
  ): Promise<ImageDeleteResponse> => {
    const { data } = await axios.post<
      ImageDeleteRequest,
      AxiosResponse<ImageDeleteResponse>
    >(BackendURLContantas.IMAGE.DELETE.PATH_NAME, imageDeleteRequest);

    return data;
  },

  total: async (imageId: string) => {
    const { data } = await axios.get<unknown, AxiosResponse<ImageTotalResponse>>(
      `${BackendURLContantas.IMAGE.TOTAL.PATH_NAME}/${imageId}`
    );

    if (!validImageTotalResponse(data)) {
      throw Error("Failed to get total score.");
    }

    return data;
  },

  avg: async (imageId: string) => {
    const { data } = await axios.get<unknown, AxiosResponse<ImageAvgResponse>>(
      `${BackendURLContantas.IMAGE.AVG.PATH_NAME}/${imageId}`
    );

    if (!validImageAvgResponse(data)) {
      throw Error("Failed to get average score.");
    }

    return data;
  },

  get: async () => {
    const { data } = await axios.get<unknown, AxiosResponse<Chat[]>>(`chat`);
    return data;
  },

  show: async (request: ImageShowRequest) => {
    const { data } = await axios.post<
      ImageShowRequest,
      AxiosResponse<ImageResponseData>
    >(BackendURLContantas.IMAGE.SHOW.PATH_NAME, request);

    return data;
  },

  find: async (request: ImageFindRequest) => {
    const { data } = await axios.post<
      ImageFindRequest,
      AxiosResponse<ImageResponseData>
    >(BackendURLContantas.IMAGE.FIND.PATH_NAME, request);

    return data;
  },

  search: async (filters: Partial<ImageSearchRequest>) => {
    const payload = normalizeImageSearchRequest(filters);

    const { data } = await axios.post<
      ImageSearchRequest,
      AxiosResponse<ImageResponse>
    >(BackendURLContantas.IMAGE.SEARCH.PATH_NAME, payload);

    return data;
  },

  following: async (request: FollowingImagesRequest, page: number = 1) => {
    const { data } = await axios.post<
      ImageRankingRequest,
      AxiosResponse<ImageResponse>
    >(`${BackendURLContantas.IMAGE.FOLLOWING.PATH_NAME}?page=${page}`, request);

    return data;
  },

  excludeFromGroupRequest: async (request: ImageExcludeFromGroupRequest) => {
    const { data } = await axios.post<
      ImageExcludeFromGroupRequest,
      AxiosResponse<ImageExcludeFromGroupResponse>
    >(BackendURLContantas.IMAGE.EXCLUDE_FROM_GROUP.PATH_NAME, request);

    return data;
  },

  scoreExists: async (request: ImageScoreExistsRequest) => {
    const { data } = await axios.post<
      ImageScoreExistsRequest,
      AxiosResponse<ImageScoreExistsReponse>
    >(BackendURLContantas.IMAGE.SCORE_EXISTS.PATH_NAME, request);

    return data;
  },

  canShowScoreBarOnPost: async (request: ImageCanShowScoreBarOnPostRequest) => {
    const { data } = await axios.post<
      ImageCanShowScoreBarOnPostRequest,
      AxiosResponse<ImageCanShowScoreBarOnPostResponse>
    >(BackendURLContantas.IMAGE.CAN_SHOW_SCOREBAR_ON_POST.PATH_NAME, request);

    return data;
  },

  bulkAction: async (
    request: ImageBulkActionRequest
  ): Promise<ImageBulkActionResponse> => {
    const { data } = await axios.post<
      ImageBulkActionRequest,
      AxiosResponse<ImageBulkActionResponse>
    >(BackendURLContantas.IMAGE.BULK_ACTION.PATH_NAME, request);

    return data;
  },

  lockedCountByUser: async (
    request: ImageLockedCountByUserRequest
  ): Promise<ImageLockedCountByUserResponse> => {
    const { data } = await axios.post<
      ImageLockedCountByUserRequest,
      AxiosResponse<ImageLockedCountByUserResponse>
    >(BackendURLContantas.IMAGE.LOCKED_COUNT_BY_USER.PATH_NAME, request);

    return data;
  },
};